import { useEffect, useRef } from 'react'
import { trackEvent } from '../../lib/analytics'
import { DISCORD_INVITE_URL } from '../../lib/discord'
import { useCommonT } from '../../i18n'

interface CommunityCTAProps {
  /** Analytics placement token - distinct per surface, same idea as GetTheAppCTA's campaign. */
  placement: string
  /** Block-variant headline. Say what is on the other side, not where the link goes. */
  headline?: string
  /** Block-variant button text / inline-variant link text. */
  label?: string
  /**
   * `block` is the full CTA (headline + button) for value moments.
   * `inline` is a bare text link for chrome like the footer.
   */
  variant?: 'block' | 'inline'
  /**
   * Fire `discord_cta_shown` on mount. Block variant only.
   *
   * Pass false where the impression count is already derivable from an event we
   * log anyway (a CTA that renders on every bracket result has exactly as many
   * impressions as there are `bracket_result` rows). Saves a row per view for a
   * number we can already get.
   */
  trackImpression?: boolean
  className?: string
}

/**
 * "Join the community" CTA.
 *
 * Replaces a bare chip reading "Discord" that sat next to Save and Share on the
 * results page. That chip rendered on 117K bracket results a month and drew
 * about 50 clicks, because the word "Discord" is a destination and not a
 * reason. Copy here should name what the person gets.
 *
 * Emits `discord_clicked` - deliberately the SAME event name the old chip used,
 * so the ~70 historical rows still join to the new ones. The `placement` prop
 * replaces the old inconsistent `from` / `source` keys, which is why those two
 * columns are read in the funnel SQL alongside this one.
 */
export default function CommunityCTA({
  placement,
  headline,
  label,
  variant = 'block',
  trackImpression = true,
  className = '',
}: CommunityCTAProps) {
  const t = useCommonT()
  const shown = useRef(false)
  const impressions = variant === 'block' && trackImpression

  useEffect(() => {
    if (!impressions || shown.current) return
    shown.current = true
    trackEvent('discord_cta_shown', { placement })
  }, [impressions, placement])

  function onClick() {
    trackEvent('discord_clicked', { placement })
  }

  const headlineText = headline ?? t('community_cta.headline')
  const labelText = label ?? t('community_cta.join')

  if (variant === 'inline') {
    return (
      <a
        href={DISCORD_INVITE_URL}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
        className={`hover:text-gray-300 transition-colors ${className}`}
      >
        {labelText}
      </a>
    )
  }

  return (
    <div
      className={`rounded-lg border border-indigo-800/50 bg-indigo-950/30 px-4 py-3 ${className}`}
    >
      <p className="text-sm text-gray-300">{headlineText}</p>
      <a
        href={DISCORD_INVITE_URL}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-500"
      >
        {labelText}
        <span aria-hidden>&rarr;</span>
      </a>
    </div>
  )
}
